import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useDeleteEvent, useEvent, usePublishEvent, useSetAccepting } from '@/api/queries';
import { Button } from '@/components/kit/Button';
import { Breadcrumbs, PageHeader } from '@/components/kit/Layout';
import { Modal } from '@/components/kit/Modal';
import { ObjectTabs } from '@/components/kit/ObjectTabs';
import { Alert, ErrorState, Note, TableSkeleton } from '@/components/kit/Note';
import { Panel, Signals } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { useToast } from '@/components/kit/Toast';
import { dateRange } from '@/lib/format';
import { PublishModal } from './PublishModal';
import { EVENT_STATE } from './state';
import { DeadlinesTab } from './tabs/DeadlinesTab';
import { MainTab } from './tabs/MainTab';
import { NominationsTab } from './tabs/NominationsTab';
import { PackagesTab } from './tabs/PackagesTab';
import { PlanTab } from './tabs/PlanTab';
import { ProgramTab } from './tabs/ProgramTab';
import { ServicesTab } from './tabs/ServicesTab';

const TABS = [
  { value: 'main', label: 'Основное' },
  { value: 'nominations', label: 'Номинации' },
  { value: 'program', label: 'Программа' },
  { value: 'services', label: 'Услуги' },
  { value: 'packages', label: 'Пакеты и цены' },
  { value: 'deadlines', label: 'Дедлайны' },
  { value: 'plan', label: 'Планирование' },
] as const;

export type TabKey = (typeof TABS)[number]['value'];

/**
 * Карточка мероприятия: семь вкладок на одном наборе данных.
 *
 * Мероприятие читается одним запросом вместе с проверками
 * и вычисляемыми значениями — вкладки не ходят в сеть каждая
 * за своим, а переключение не ждёт загрузки.
 */
export function EventPage() {
  const { eventId, tab } = useParams<{ eventId: string; tab?: TabKey }>();
  const navigate = useNavigate();
  const toast = useToast();

  const query = useEvent(eventId);
  const publish = usePublishEvent(eventId!);
  const setAccepting = useSetAccepting(eventId!);
  const remove = useDeleteEvent();
  const [publishOpen, setPublishOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const active: TabKey = TABS.some((t) => t.value === tab) ? (tab as TabKey) : 'main';

  if (query.isError) {
    return (
      <div className="main">
        <Panel>
          <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
        </Panel>
      </div>
    );
  }

  if (query.isLoading || !query.data) {
    return (
      <div className="main">
        <PageHeader title="Мероприятие" />
        <Panel flush>
          <TableSkeleton rows={8} cols={5} />
        </Panel>
      </div>
    );
  }

  const event = query.data;
  const state = EVENT_STATE[event.state];
  const passed = event.readiness.filter((c) => c.ok).length;
  const total = event.readiness.length;

  const goTab = (next: TabKey) => navigate(`/events/${event.id}/${next}`);

  const doPublish = () =>
    publish.mutate(undefined, {
      onSuccess: () => {
        setPublishOpen(false);
        toast.show(`«${event.city}» опубликовано`);
      },
      onError: (e) =>
        toast.showError(e instanceof ApiError ? e.message : 'Опубликовать не удалось'),
    });

  const toggleAccepting = () =>
    setAccepting.mutate(!event.accepting, {
      onSuccess: () =>
        toast.show(event.accepting ? 'Приём заявок остановлен' : 'Приём заявок открыт'),
      onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не получилось'),
    });

  return (
    <div className="main has-abar">
      <Breadcrumbs
        items={[
          { label: 'Мероприятия', onClick: () => navigate('/events') },
          { label: event.city },
        ]}
      />

      <PageHeader
        title={`YouChamp · ${event.city}`}
        before={
          <>
            <span className="dim">
              {dateRange(event.dateFrom, event.dateTo)}
            </span>
            <Pill tone={state.tone}>{state.label}</Pill>
            {event.state === 'live' && !event.accepting ? (
              <Pill tone="warn">Приём остановлен</Pill>
            ) : null}
          </>
        }
      >
        {/* Удаляется только черновик — со всем содержимым.
            Опубликованное остаётся: по нему уже могли пойти
            заявки и сделки в других модулях (БЛ §4.1). */}
        {event.state === 'draft' ? (
          <>
            <Button tone="danger" onClick={() => setDeleteOpen(true)}>
              Удалить
            </Button>
            <Button tone="primary" onClick={() => setPublishOpen(true)}>
              Опубликовать…
            </Button>
          </>
        ) : event.state === 'live' ? (
          <Button tone="quiet" onClick={toggleAccepting} disabled={setAccepting.isPending}>
            {event.accepting ? 'Остановить приём' : 'Открыть приём'}
          </Button>
        ) : null}
      </PageHeader>

      <Signals>
        {event.state === 'draft' ? (
          <Alert
            action={
              <Button small tone="quiet" onClick={() => setPublishOpen(true)}>
                Что осталось
              </Button>
            }
          >
            <b>
              Готово {passed} из {total} проверок к публикации.
            </b>
            <span>Пока мероприятие в черновике, отдел продаж его не видит</span>
          </Alert>
        ) : null}
      </Signals>

      <ObjectTabs items={[...TABS]} value={active} onChange={goTab} />

      {active === 'main' ? <MainTab event={event} /> : null}
      {active === 'nominations' ? <NominationsTab event={event} onGoTab={goTab} /> : null}
      {active === 'program' ? <ProgramTab event={event} /> : null}
      {active === 'services' ? <ServicesTab event={event} /> : null}
      {active === 'packages' ? <PackagesTab event={event} /> : null}
      {active === 'deadlines' ? <DeadlinesTab event={event} /> : null}
      {active === 'plan' ? <PlanTab event={event} /> : null}

      {publishOpen ? (
        <PublishModal
          event={event}
          onClose={() => setPublishOpen(false)}
          onPublish={doPublish}
          pending={publish.isPending}
        />
      ) : null}

      {deleteOpen ? (
        <Modal
          title={`Удалить «${event.city}»?`}
          onClose={() => setDeleteOpen(false)}
          confirm={{
            label: 'Удалить',
            danger: true,
            disabled: remove.isPending,
            onClick: () =>
              remove.mutate(event.id, {
                onSuccess: () => {
                  toast.show(`«${event.city}» удалено`);
                  navigate('/events');
                },
                onError: (e) => {
                  setDeleteOpen(false);
                  toast.showError(e instanceof ApiError ? e.message : 'Не удалось удалить');
                },
              }),
          }}
        >
          <p className="txt">
            Удалится вместе со всем содержимым: {event.nominations.length} номинаций,{' '}
            {event.services.length} услуг, {event.blocks.length} блоков программы,{' '}
            {event.packages.length} пакетов, браслеты, дедлайны и план.
          </p>
          <Note tone="crit">Действие необратимо</Note>
        </Modal>
      ) : null}
    </div>
  );
}
